// The demo league (scenario O6, docs/design/PLAN-2026-10-09-demo-league.md).
//
// A real league in the database, held still at the Tuesday morning after a
// week's games, that anybody can look around with no account. Its screens are
// the ordinary league screens reading the ordinary API: the server answers its
// reads as the seat a visitor sits in, at the moment it is held at, and
// refuses every write to it.
//
// WHAT THIS FILE ADDS IS SMALL ON PURPOSE. The league's card, from
// `GET /api/demo`, so the shell and the screens know which league `/l/demo`
// is without a hub (a visitor has none); and the moment it is held at, so a
// time this browser tells — "2 days ago", "locks in 2d 5h" — is told from that
// Tuesday and not from today. Nothing here simulates anything.
//
// READ ONLY WHILE ON IT. The card is asked for when the address is the demo
// league's, and kept, so leaving and coming back does not ask again.

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { DEMO_LEAGUE_ID, DemoView, valueOf, type LeagueCard } from '@fsp/domain';
import { useRead, type Read } from './read';
import { leagueIdFromPath } from './route';

const DemoContext = createContext<Read<DemoView | null> | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const onIt = leagueIdFromPath(pathname) === DEMO_LEAGUE_ID;
  const read = useRead(onIt ? '/demo' : null, DemoView);
  return <DemoContext.Provider value={read}>{children}</DemoContext.Provider>;
}

/** The demo league's read: loading until asked and answered, then its card and moment. */
export function useDemo(): Read<DemoView | null> {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside DemoProvider');
  return ctx;
}

/**
 * Where the demo league opens: the matchup, on the weekend just played. A
 * visitor comes on a Tuesday morning to see how the weekend went (Ken,
 * 2026-10-09, SCENARIOS O6), and the league's own week is already the next.
 * `weekend` asks for that week once (`DemoLeague` in App.tsx), so stepping
 * to the current week afterwards stays there.
 */
export const DEMO_PATH = `/l/${DEMO_LEAGUE_ID}/matchup?weekend`;

/** Whether a league id is the demo league's. */
export function isDemoLeague(leagueId: string | null | undefined): boolean {
  return leagueId === DEMO_LEAGUE_ID;
}

/** The demo league's card, once it has arrived; null before, and on any other league. */
export function useDemoCard(): LeagueCard | null {
  const { snapshot } = useDemo();
  return useMemo(() => valueOf(snapshot)?.card ?? null, [snapshot]);
}

/**
 * The moment the league on screen stands at, in milliseconds: the demo
 * league's held instant on its pages, and the wall's everywhere else.
 *
 * For a time this browser tells. A league's own facts — which week it is,
 * whether a slot is locked — come from the server, which answers the demo's
 * reads at that instant already; this is for the sentences built here from a
 * timestamp and "now".
 */
export function useLeagueNow(): number {
  const { pathname } = useLocation();
  const { snapshot } = useDemo();
  const held = isDemoLeague(leagueIdFromPath(pathname)) ? valueOf(snapshot)?.frozenAt : undefined;
  return held === undefined ? Date.now() : Date.parse(held);
}
